import { useState, useMemo } from 'react';
import { Loader2, Download, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNewsIntelligence, NewsFilterTab, NewsIntelligenceRow } from '@/hooks/useNewsIntelligence';

import { NewsFilterSidebar } from '@/components/news/NewsFilterSidebar';
import { SortOption, DateRangePreset } from '@/components/news/NewsIntelligenceFilters';
import { NewsIntelligenceTable } from '@/components/news/NewsIntelligenceTable';
import { ArticleDetailModal } from '@/components/news/ArticleDetailModal';
import { convertToCSV, downloadFile, formatDateForFilename } from '@/lib/export-utils';

const PAGE_SIZE = 50;

export default function News() {
  // Filter state
  const [activeTab, setActiveTab] = useState<NewsFilterTab>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [signalType, setSignalType] = useState('all');
  const [priority, setPriority] = useState('all');
  const [hasProspect, setHasProspect] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangePreset>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [page, setPage] = useState(1);

  // Detail modal
  const [selectedArticle, setSelectedArticle] = useState<NewsIntelligenceRow | null>(null);

  // Data fetching
  const priorityFilter = priority === 'high' ? 1 : priority === 'medium' ? 2 : priority === 'low' ? 3 : undefined;
  const { data: articles, isLoading } = useNewsIntelligence(activeTab, {
    searchTerm,
    priorityLevel: priorityFilter,
  });
  

  // Client-side filtering for signal type, prospect, date range
  const filteredArticles = useMemo(() => {
    let result = articles || [];

    if (signalType !== 'all') {
      result = result.filter(a => a.signal_type === signalType);
    }
    if (hasProspect) {
      result = result.filter(a => a.prospect_company_name);
    }
    if (dateRange !== 'all') {
      const now = new Date();
      const daysAgo = dateRange === '7days' ? 7 : 30;
      const since = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
      result = result.filter(a => a.published_at && new Date(a.published_at) >= since);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          return (a.priority_level || 99) - (b.priority_level || 99);
        case 'relevance':
          return (b.relevance_score || 0) - (a.relevance_score || 0);
        case 'newest':
        default:
          return new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime();
      }
    });

    return result;
  }, [articles, signalType, hasProspect, dateRange, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / PAGE_SIZE);
  const paginatedArticles = filteredArticles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hasActiveFilters = signalType !== 'all' || priority !== 'all' || hasProspect || dateRange !== 'all' || searchTerm !== '';

  const clearAllFilters = () => {
    setSearchTerm('');
    setSignalType('all');
    setPriority('all');
    setHasProspect(false);
    setDateRange('all');
    setSortBy('newest');
    setPage(1);
  };

  const handleExport = () => {
    const csv = convertToCSV(filteredArticles.map(a => ({
      title: a.title,
      source: a.source_feed,
      published_at: a.published_at,
      priority: a.priority_label,
      signal_type: a.signal_type,
      prospect: a.prospect_company_name,
      url: a.url,
    })));
    downloadFile(csv, `news-intelligence-${formatDateForFilename()}.csv`, 'text/csv;charset=utf-8;');
  };


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">News Intelligence</h1>
          <p className="text-muted-foreground">
            Monitor industry news and market signals
            {hasActiveFilters && ` (filtered from ${(articles || []).length.toLocaleString()})`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={filteredArticles.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Grid layout: sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Filter Sidebar */}
        <div className="lg:col-span-1">
          <NewsFilterSidebar
            activeTab={activeTab}
            onTabChange={(tab) => { setActiveTab(tab); setPage(1); }}
            searchTerm={searchTerm}
            onSearchChange={(v) => { setSearchTerm(v); setPage(1); }}
            signalType={signalType}
            onSignalTypeChange={(v) => { setSignalType(v); setPage(1); }}
            priority={priority}
            onPriorityChange={(v) => { setPriority(v); setPage(1); }}
            hasProspect={hasProspect}
            onHasProspectChange={(v) => { setHasProspect(v); setPage(1); }}
            dateRange={dateRange}
            onDateRangeChange={(v) => { setDateRange(v); setPage(1); }}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onClearAll={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            totalCount={filteredArticles.length}
          />
        </div>

        {/* Right: Content */}
        <div className="lg:col-span-3 space-y-6">

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : paginatedArticles.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border rounded-lg">
              <Newspaper className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No articles found</p>
              <p className="text-xs mt-1">News will appear here once synced from the BioCreative pipeline</p>
            </div>
          ) : (
            <NewsIntelligenceTable
              data={paginatedArticles}
              onViewArticle={setSelectedArticle}
            />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filteredArticles.length)} of {filteredArticles.length}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Article Detail Modal */}
      <ArticleDetailModal
        article={selectedArticle}
        open={!!selectedArticle}
        onOpenChange={(open) => !open && setSelectedArticle(null)}
      />
    </div>
  );
}
